import { ChevronLeft, ListFilterPlus, MessageCircleMore, MessageCircleOff, MinusCircle, Reply, Send, Trash2 , X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom';
import { useUserContext } from '../contexts/user';
import { usePopUpContext } from '../contexts/Popup';
import { get, off, onChildAdded, onChildRemoved, ref, remove, set } from 'firebase/database';
import { db } from '../Database';
import { useLoaderContext } from '../contexts/loader';
import { useCheckForNewToken } from '../checkForNewToken';
import isArabic from 'is-arabic';
import { v4 as uuid } from "uuid";

const Chat = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [messages, setMessages] = useState([]);
    const [choosedQuestion, setChoosedQuestion] = useState({});
    const [replyOn, setReplyOn] = useState(-1);
    const [quizData, setQuizData] = useState({});
    const [loadingData, setLoadingData] = useState(false);
    const [chooseQuestion, setChooseQuestion] = useState(false);
    const [isSendingMessage, setIsSendingMessage] = useState(false);
    const [replyMessageId, setReplyMessageId] = useState("");
    const [showTip , setShowTip] = useState(true);

    const { userInfo, user, userJWT, setUserInfo, setUserIndex, setUser, setUserJWT, appLanguage } = useUserContext();
    const { setMessageContent } = usePopUpContext();
    const { setIsLoading } = useLoaderContext();
    const { checkForNewToken } = useCheckForNewToken();

    const messageInput = useRef(null);
    const contentRef = useRef(null);
    const choosedQuestionIndex = useRef(-1);
    const messagesContainerRef = useRef();

    const scrollToBottom = () => {
        const container = messagesContainerRef.current;
        if (container) container.scrollTop = container.scrollHeight;
    }

    useEffect(() => {
        scrollToBottom();
    }, [messages])

    useEffect(() => {
        const fetchQuizData = async (isTeacher = null) => {
            setLoadingData(true);
            setIsLoading(prev => prev + 1);
            await fetch("https://quiz-battle-api.vercel.app/api/quiz-data/" + id, {
                method: "GET",
                credentials: "include",
                headers: {
                    "Authorization": `Bearer ${userJWT}`,
                    "Content-Type": "application/json",
                },
            })
                .then(res => res.json())
                .then(data => {
                    checkForNewToken(data, setUserJWT, setUserInfo, setUser, setUserIndex, navigate, setMessageContent);
                    setIsLoading(prev => prev !== 0 ? prev - 1 : prev);
                    if (data.failed) {
                        setMessageContent({
                            title: appLanguage === "ar" ? "فشل جلب البيانات" : "Fetching Data Failed",
                            message: data.msg,
                        })
                        if (userInfo.role === "teacher") {
                            navigate("/home");
                        } else {
                            navigate("/info/" + id);
                        }
                    } else {
                        setLoadingData(false);
                        setQuizData(data.data)
                        if (isTeacher === "check if teacher") {
                            if (data.data.from_id !== userInfo.id) {
                                navigate("/home");
                                setMessageContent({
                                    title: appLanguage === "ar" ? "تم رفض الوصول" : "Access Denied",
                                    message: appLanguage === "ar" ? "لا يمكنك الوصول إلى هذه الصفحة لأن المعلم والطلاب الذين قاموا بحل هذا الاختبار فقط يمكنهم الوصول إليها." : "You can't access this page because only the teacher and the students who solved this quiz can access this page.",
                                })
                            }
                        }
                    }
                }).catch(() => {
                    setIsLoading(prev => prev !== 0 ? prev - 1 : prev);
                    setMessageContent({
                        title: appLanguage === "ar" ? "فشل جلب البيانات" : "Fetching Data Failed",
                        message: appLanguage === "ar" ? "عذراً! حدث خطأ ما أثناء جلب بيانات الاختبار." : "Oops! something went wrong while fetching quiz data.",
                    })
                    navigate("/home");
                })
        }
        const handleMessages = (snapShot) => {
            const message = snapShot.val();
            setMessages(prev => [...prev.sort((a , b) => a.sent_at - b.sent_at), message]);
            scrollToBottom();
        }

        const handleMessageRemoved = (snapShot) => {
            const message = snapShot.val();
            if (replyOn !== -1 && messages[replyOn].message_id === message.message_id) {
                setReplyOn(-1);
            }
            setMessages(prevMessages => {
                const isInMessages = prevMessages.findIndex(msg => msg.message_id === message.message_id);
                if (isInMessages !== -1) {
                    return prevMessages.filter(msg => msg.message_id !== message.message_id).sort((a , b) => a.sent_at - b.sent_at);
                }
                return prevMessages.sort((a , b) => a.sent_at - b.sent_at);
            });
        };

        if (userInfo.playedQuizzes && userInfo.playedQuizzes.includes(id)) {
            onChildAdded(ref(db, "chats/" + id + "/messages"), handleMessages)
            onChildRemoved(ref(db, "chats/" + id + "/messages"), handleMessageRemoved);
            fetchQuizData();
        } else {
            if (userInfo.role && userInfo.role === "teacher") {
                fetchQuizData("check if teacher")
                onChildAdded(ref(db, "chats/" + id + "/messages"), handleMessages)
                onChildRemoved(ref(db, "chats/" + id + "/messages"), handleMessageRemoved);
            } else {
                if (userInfo.role) {
                    navigate("/info/" + id);
                    setMessageContent({
                        title: appLanguage === "ar" ? "تم رفض الوصول" : "Access Denied",
                        message: appLanguage === "ar" ? "لا يمكنك الوصول إلى هذه الصفحة لأن المعلم والطلاب الذين قاموا بحل هذا الاختبار فقط يمكنهم الوصول إليها." : "You can't access this page because only the teacher and the students who solved this quiz can access this page.",
                    })
                }
            }
        }
        return () => {
            setMessages([]);
            off(ref(db, "chats/" + id + "/messages"), "child_added", handleMessages);
            off(ref(db, "chats/" + id + "/messages"), "child_removed", handleMessageRemoved);
        }
    }, [userInfo])

    const sendMessage = () => {
        const message = messageInput.current.value?.trim();
        if (message) {
            setIsSendingMessage(true);
            const messageContent = {
                text: message,
                sent_at: new Date().getTime(),
                sender_id: userInfo.known_id,
                sender_name: userInfo.username,
                question: choosedQuestion,
                reply: replyOn,
                message_id: uuid(),
                reply_message_id: replyMessageId,
            };
            set(ref(db, "chats/" + id + "/messages/" + messageContent.message_id), messageContent)
                .then(() => {
                    setIsSendingMessage(false);
                    setReplyOn(-1);
                    setChoosedQuestion({});
                    choosedQuestionIndex.current = -1;
                    messageInput.current.value = "";
                    messageInput.current.dir = "ltr";
                })
        } else {
            setMessageContent({
                title: appLanguage === "ar" ? "إدخال فارغ" : "Empty Input",
                message: appLanguage === "ar" ? "لا يمكنك إرسال رسالة بدون نص." : "You can't send a message without a text.",
            })
        }
    }

    const hideContainer = (e) => {
        const { clientX, clientY } = e;
        const contentRect = contentRef.current.getBoundingClientRect();
        if (
            clientX > contentRect.right ||
            clientX < contentRect.left ||
            clientY > contentRect.bottom ||
            clientY < contentRect.top
        ) {
            setChooseQuestion(false);
        }
    }

    const removeMessage = (message_id) => {
        setIsSendingMessage(true);
        remove(ref(db, "chats/" + id + "/messages/" + message_id))
            .then(() => {
                setIsSendingMessage(false);
            })
    }

    return (
        <div className="chat_container">
            <header>
                <ChevronLeft onClick={() => navigate((userInfo.role === "teacher" ? "/details/" : "/info/") + id)} />
                <p className="title">{appLanguage === "ar" ? "الدردشة" : "Chat"}</p>
            </header>
            <div className="content">
                <div className="messages" ref={messagesContainerRef}>
                    {
                        !messages.length ?
                            <div className="no_messages">
                                <MessageCircleMore strokeWidth={2} size={50} />
                                <p>{appLanguage === "ar" ? "لا توجد رسائل بعد. ابدأ المحادثة وكن أول من يرسل رسالة!" : "No messages yet. Start the conversation and be the first to send a message!"}</p>
                            </div> :
                            messages.map((message, i) => {
                                return (
                                    <div key={message.message_id} className={"message " + (message.sender_id !== userInfo.known_id ? "friend" : "")}>
                                        <div className="main_content">
                                            {message.sender_id !== userInfo.known_id ? <p className="sender" dir={isArabic(message.sender_name.split(" ")[0], { count: 0, }) ? "rtl" : "ltr"}>{message.sender_name}</p> : undefined}
                                            {
                                                message.reply !== -1 ?
                                                    (!messages[messages.findIndex(m => m.message_id === message.reply_message_id)] ?
                                                        <div className="reply">
                                                            <p className="from">{appLanguage === "ar" ? "غير موجود" : "Not Found"}</p>
                                                            <p className="message_value">{appLanguage === "ar" ? "الرسالة غير متوفرة" : "Message is unavailable"}</p>
                                                        </div>
                                                        :
                                                        <div className="reply">
                                                            <p className="from" dir={isArabic(messages[messages.findIndex(m => m.message_id === message.reply_message_id)]?.sender_name.split(" ")[0], { count: 0, }) ? "rtl" : "ltr"}>{messages[messages.findIndex(m => m.message_id === message.reply_message_id)] ? messages[messages.findIndex(m => m.message_id === message.reply_message_id)].sender_name : (appLanguage === "ar" ? "غير محدد" : "Undefined")}</p>
                                                            <p className="message_value" dir={isArabic(messages[messages.findIndex(m => m.message_id === message.reply_message_id)]?.text.split(" ")[0], { count: 0, }) ? "rtl" : "ltr"}>{messages[messages.findIndex(m => m.message_id === message.reply_message_id)].text || "Couldn't Load Message Text."}</p>
                                                        </div>
                                                    )
                                                    : undefined
                                            }
                                            {
                                                message.question ?
                                                    <div className="question">
                                                        <p className="text" dir={isArabic(message.question.question.split(" ")[0], { count: 0, }) ? "rtl" : "ltr"}>{message.question.question}</p>
                                                        <div className="answers">
                                                            <button className={"answer " + (message.question.answers[0] === message.question.correctAnswer ? "correct_answer" : undefined)} dir={isArabic(message.question.answers[0].split(" ")[0], { count: 0, }) ? "rtl" : "ltr"}>{message.question.answers[0]}</button>
                                                            <button className={"answer " + (message.question.answers[1] === message.question.correctAnswer ? "correct_answer" : undefined)} dir={isArabic(message.question.answers[1].split(" ")[0], { count: 0, }) ? "rtl" : "ltr"}>{message.question.answers[1]}</button>
                                                            <button className={"answer " + (message.question.answers[2] === message.question.correctAnswer ? "correct_answer" : undefined)} dir={isArabic(message.question.answers[2].split(" ")[0], { count: 0, }) ? "rtl" : "ltr"}>{message.question.answers[2]}</button>
                                                            <button className={"answer " + (message.question.answers[3] === message.question.correctAnswer ? "correct_answer" : undefined)} dir={isArabic(message.question.answers[3].split(" ")[0], { count: 0, }) ? "rtl" : "ltr"}>{message.question.answers[3]}</button>
                                                        </div>
                                                    </div> : undefined
                                            }
                                            <p className="msg_text" dir={isArabic(message.text?.split(" ")[0], { count: 0, }) ? "rtl" : "ltr"}>{message.text}</p>
                                            {message.sender_id === userInfo.known_id ? <button className="delete_message" onClick={() => removeMessage(message.message_id)}><Trash2 size={16} /></button> : <button onClick={() => { setReplyMessageId(message.message_id); setReplyOn(i) }} className="reply"><Reply size={16} /></button>}
                                        </div>
                                    </div>
                                )
                            })
                    }
                </div>
                {userInfo.role === "teacher" && showTip ? <div className="tip">
                    <p>Join our <a href="https://quiz-battle-connect.netlify.app" target="_blank">community</a> for full chat access.</p>
                    <X onClick={() => setShowTip(false)}/>                
                </div> : undefined}
                <div className="send_message">
                    <div className="message_content">
                        {replyOn !== -1 ? <div className="reply">
                            <div className="reply_content">
                                <p className="from" dir={isArabic(messages[messages.findIndex(m => m.message_id === replyMessageId)]?.sender_name.split(" ")[0], { count: 0, }) ? "rtl" : "ltr"}>{messages[messages.findIndex(m => m.message_id === replyMessageId)] ? messages[messages.findIndex(m => m.message_id === replyMessageId)].sender_name : (appLanguage === "ar" ? "غير موجود" : "Not Found")}</p>
                                <p className="message_value" dir={isArabic(messages[replyOn]?.text.split(" ")[0], { count: 0, }) ? "rtl" : "ltr"}>{messages[messages.findIndex(m => m.message_id === replyMessageId)]?.text || "Couldn't load message text."}</p>
                            </div>
                            <button className="remove_reply" onClick={() => setReplyOn(-1)}><MinusCircle /></button>
                        </div> : undefined}
                        {
                            choosedQuestion.question ?
                                <div className="question">
                                    <p className="question" dir={isArabic(choosedQuestion.question.split(" ")[0], { count: 0, }) ? "rtl" : "ltr"}>{choosedQuestion.question}</p>
                                    <button className="remove_question" disables={isSendingMessage} onClick={() => { setChoosedQuestion({}); choosedQuestionIndex.current = -1; }}><MinusCircle /></button>
                                </div> : undefined
                        }
                    </div>
                    <div className="send_content">
                        <input ref={messageInput} onChange={e => {
                            if (isArabic(e.target.value.split(" ")[0], { count: 0, }) && e.target.value.trim()) {
                                e.target.dir = "rtl";
                            } else {
                                e.target.dir = "ltr";
                            }
                        }} type="text" placeholder={appLanguage === "ar" ? "اكتب رسالة..." : "Write a message..."} />
                        <div className="buttons">
                            <button onClick={() => setChooseQuestion(true)} text={appLanguage === "ar" ? "إضافة سؤال" : "Add Question"}><ListFilterPlus size={18} /></button>
                            <button onClick={sendMessage} className="send"><Send size={18} /></button>
                        </div>
                    </div>
                </div>
            </div>
            {
                chooseQuestion ?
                    <div className="choose_question_container" onClick={hideContainer}>
                        <div className="questions_content" ref={contentRef}>
                            <p className="title">{appLanguage === "ar" ? "الأسئلة" : "Questions"}</p>
                            <div className="questions">
                                {
                                    quizData.questions && quizData.questions.length ?
                                        quizData.questions.map((question, i) => {
                                            return (
                                                <div className="question" key={"question_" + i}>
                                                    <p className="question_text" dir={isArabic(question.question.split(" ")[0], { count: 0, }) ? "rtl" : "ltr"}>{question.question}</p>
                                                    <div className="answers">
                                                        <button className={question.answers[0] === question.correctAnswer ? "correct_answer" : ""} dir={isArabic(question.answers[0].split(" ")[0], { count: 0, }) ? "rtl" : "ltr"}>{question.answers[0]}</button>
                                                        <button className={question.answers[1] === question.correctAnswer ? "correct_answer" : ""} dir={isArabic(question.answers[1].split(" ")[0], { count: 0, }) ? "rtl" : "ltr"}>{question.answers[1]}</button>
                                                        <button className={question.answers[2] === question.correctAnswer ? "correct_answer" : ""} dir={isArabic(question.answers[2].split(" ")[0], { count: 0, }) ? "rtl" : "ltr"}>{question.answers[2]}</button>
                                                        <button className={question.answers[3] === question.correctAnswer ? "correct_answer" : ""} dir={isArabic(question.answers[3].split(" ")[0], { count: 0, }) ? "rtl" : "ltr"}>{question.answers[3]}</button>
                                                    </div>
                                                    <button onClick={() => {
                                                        if (choosedQuestionIndex.current === i) {
                                                            setChoosedQuestion({})
                                                            choosedQuestionIndex.current = -1;
                                                        } else {
                                                            setChoosedQuestion(question)
                                                            choosedQuestionIndex.current = i;
                                                        }
                                                    }} className={"select_question " + (choosedQuestionIndex.current === i ? "active" : "")}>{choosedQuestionIndex.current === i ? (appLanguage === "ar" ? "إلغاء التحديد" : "Deselect") : (appLanguage === "ar" ? "تحديد" : "Select")}</button>
                                                </div>
                                            )
                                        })
                                        : loadingData ? undefined : <span className="loading"></span>
                                }
                                {loadingData ? <span className="loading"></span> : undefined}
                            </div>
                        </div>
                    </div> : undefined
            }
        </div>
    )
}

export default Chat;